import type { PipelineDefinition } from './PipelineDefinition';
import type { PipelineMetadata } from './PipelineMetadata';

export class PipelineRegistry {
  private definitions: Map<string, PipelineDefinition> = new Map();
  private initialized = false;
  private standardPipelinesLoader: (() => PipelineDefinition[]) | null = null;

  constructor() {}

  /**
   * Set the loader function for standard pipelines (called by index.ts to avoid circular dependency)
   */
  setStandardPipelinesLoader(loader: () => PipelineDefinition[]): void {
    this.standardPipelinesLoader = loader;
  }

  /**
   * Lazily registers built-in pipelines on first call that needs them.
   * Called internally before reads; callers don't need to call this.
   */
  private ensureInitialized(): void {
    if (this.initialized) return;
    this.initialized = true;

    if (!this.standardPipelinesLoader) {
      console.warn('[PipelineRegistry] No standard pipelines loader configured. Using defaults.');
      return;
    }

    const standardPipelines = this.standardPipelinesLoader();
    for (const definition of standardPipelines) {
      // Do not overwrite user-registered pipelines
      if (!this.definitions.has(definition.metadata.id)) {
        this.definitions.set(definition.metadata.id, definition);
      }
    }
  }

  /**
   * Register a pipeline definition.
   * Call this before constructing EdgeStreamHubAdapter if using custom IDs.
   */
  register(definition: PipelineDefinition): void {
    if (this.definitions.has(definition.metadata.id)) {
      console.warn(
        `[PipelineRegistry] Overwriting existing pipeline: ${definition.metadata.id}`
      );
    }
    this.definitions.set(definition.metadata.id, definition);
  }

  /** Retrieve a pipeline definition by ID. Returns undefined if not found. */
  get(id: string): PipelineDefinition | undefined {
    this.ensureInitialized();
    return this.definitions.get(id);
  }

  /** Get all registered pipeline definitions. */
  getAll(): PipelineDefinition[] {
    this.ensureInitialized();
    return Array.from(this.definitions.values());
  }

  /** Get all pipelines for a given direction (also returns 'bidirectional' for any query). */
  getForDirection(
    direction: PipelineMetadata['direction']
  ): PipelineDefinition[] {
    this.ensureInitialized();
    return Array.from(this.definitions.values()).filter(
      (d) =>
        d.metadata.direction === direction ||
        d.metadata.direction === 'bidirectional'
    );
  }
}

// Module-level default instance for edge-stream-js-react's own pipelines
export const edgeStreamRegistry = new PipelineRegistry();

/** Backward-compatible accessor. Returns the default edge-stream-js-react registry. */
export function getPipelineRegistry(): PipelineRegistry {
  return edgeStreamRegistry;
}
